import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Menu } from 'primereact/menu';
import { Sidebar } from 'primereact/sidebar';
import { useCart } from '../context/CartContext';
import { useClienteAuth } from '../context/ClienteAuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { getProductImageUrl } from '../utils/imageUtils';
import { EcommerceCategoriaService } from '../services/EcommerceCategoriaService';
import { EcommerceEmpresaService } from '../services/EcommerceEmpresaService';
import CategoryMegaMenu from './CategoryMegaMenu';
import SearchButton from './SearchButton';
import AuthDialog from './AuthDialog';

const EcommerceNavbar = () => {
    const navigate = useNavigate();
    const { getItemCount, setCartVisible } = useCart();
    const { cliente, clienteLogout } = useClienteAuth();
    const { favorites, toggleFavorite } = useFavorites();
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
    const [categoryDrawerVisible, setCategoryDrawerVisible] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [authDialogVisible, setAuthDialogVisible] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const [favoritesVisible, setFavoritesVisible] = useState(false);
    const searchInputRef = useRef(null);
    const userMenuRef = useRef(null);
    const idEmpresa = 1;

    // Cargar datos de empresa y categorías del backend
    useEffect(() => {
        const loadData = async () => {
            try {
                const [empresaResponse, categoriesResponse] = await Promise.all([
                    EcommerceEmpresaService.obtenerEmpresaPorId(idEmpresa),
                    EcommerceCategoriaService.obtenerCategorias(idEmpresa)
                ]);
                
                setEmpresa(empresaResponse);
                setCategories(categoriesResponse || []);
                if (categoriesResponse && categoriesResponse.length > 0) {
                    setHoveredCategory(categoriesResponse[0]);
                }
            } catch (error) {
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const itemCount = getItemCount();

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/tienda/catalogo?q=${encodeURIComponent(searchQuery.trim())}`);
            setMobileSearchVisible(false);
            setCategoryDrawerVisible(false);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch(e);
        }
    };

    const handleCategoryClick = (catId) => {
        navigate(`/tienda/catalogo?cat=${catId}`);
        setMobileMenuVisible(false);
        setCategoryDrawerVisible(false);
    };

    const userMenuItems = [
        {
            label: 'Perfil',
            icon: 'pi pi-user',
            command: () => navigate('/tienda/perfil')
        },
        // {
        //     label: 'Mis compras',
        //     icon: 'pi pi-shopping-bag',
        //     command: () => navigate('/tienda/mis-compras')
        // },
        // {
        //     label: 'Configuración',
        //     icon: 'pi pi-cog',
        //     command: () => navigate('/tienda/configuracion')
        // },
        {
            separator: true
        },
        {
            label: 'Cerrar sesión',
            icon: 'pi pi-sign-out',
            command: async () => {
                await clienteLogout();
                navigate('/tienda');
            }
        }
    ];

    return (
        <nav className="ec-navbar">
            {/* Top Bar */}
            <div className="ec-navbar-top">
                <div className="ec-container ec-navbar-top-content">
                    <span>Bienvenido a {empresa?.nombre || 'TechStore'} — Envíos a todo el país</span>
                    <div className="ec-navbar-top-links">
                        {/* 
                        <a href="#rastrear"><i className="pi pi-map-marker" style={{ marginRight: 4 }}></i> Rastrear pedido</a>
                        <a href="#ofertas"><i className="pi pi-tag" style={{ marginRight: 4 }}></i> Todas las ofertas</a> 
                        */}
                    </div>
                </div>
            </div>

            {/* Main Nav */}
            <div className="ec-navbar-main">
                <div className="ec-container ec-navbar-main-content">
                    {/* Logo + Botón Menú (columna izquierda del grid) */}
                    <div className="ec-navbar-left">
                        <div className="ec-logo" onClick={() => navigate('/tienda')} style={{ cursor: 'pointer' }}>
                            <div className="ec-logo-icon">
                                <i className="pi pi-bolt"></i>
                            </div>
                            <span className="ec-logo-text">{empresa?.nombre?.split(' ')[0]}<span>{empresa?.nombre?.split(' ')[1] || 'Store'}</span></span>
                        </div>

                        <button
                            className="ec-menu-btn"
                            onClick={() => setCategoryDrawerVisible(true)}
                        >
                            <i className="pi pi-bars" />
                            <span>Menú</span>
                        </button>
                    </div>

                    {/* Search Bar - Desktop */}
                    <div className="ec-search-bar">
                        <InputText
                            ref={searchInputRef}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Buscar productos, marcas, accesorios..."
                        />
                        <SearchButton onClick={handleSearch} />
                    </div>

                    {/* Actions */}
                    <div className="ec-navbar-actions">
                        {cliente ? (
                            <>
                                <Button
                                    className="ec-nav-btn ec-user-btn"
                                    onClick={(e) => userMenuRef.current && userMenuRef.current.toggle(e)}
                                    tooltip="Mi cuenta"
                                    tooltipOptions={{ position: 'bottom' }}
                                >
                                    <div style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold',
                                        border: '1.5px solid rgba(255,255,255,0.5)',
                                        flexShrink: 0,
                                        minWidth: '32px'
                                    }}>
                                        {(cliente.nombre || cliente.username).charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: 0
                                    }}>
                                        <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>Hola,</span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: '600' }}>
                                            {cliente.nombre || cliente.username}
                                        </span>
                                    </div>
                                    <i className="pi pi-chevron-down" />
                                </Button>
                                <Menu model={userMenuItems} popup ref={userMenuRef} />
                            </>
                        ) : (
                            <Button
                                icon="pi pi-user"
                                className="ec-nav-btn"
                                onClick={() => setAuthDialogVisible(true)}
                                tooltip="Iniciar Sesión"
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        )}

                        <Button
                            icon="pi pi-heart"
                            className="ec-nav-btn"
                            onClick={() => {
                                if (!cliente) {
                                    setAuthDialogVisible(true);
                                } else {
                                    setFavoritesVisible(true);
                                }
                            }}
                            tooltip={cliente ? "Mis favoritos" : "Inicia sesión para guardar favoritos"}
                            tooltipOptions={{ position: 'bottom' }}
                        />

                        <Button
                            icon="pi pi-shopping-cart"
                            className="ec-nav-btn p-overlay-badge"
                            onClick={() => setCartVisible(true)}
                            tooltip="Carrito"
                            tooltipOptions={{ position: 'bottom' }}
                        >
                            {itemCount > 0 && (
                                <Badge value={itemCount} severity="danger" />
                            )}
                        </Button>

                        {/* Mobile menu toggle */}
                        <Button
                            icon="pi pi-bars"
                            className="ec-nav-btn"
                            style={{ display: 'none' }}
                            id="ec-mobile-menu-btn"
                            onClick={() => setMobileMenuVisible(true)}
                        />
                    </div>
                </div>

                {/* Mobile Search Bar (expandable) */}
                {mobileSearchVisible && (
                    <div className="ec-container" style={{ paddingBottom: 12 }}>
                        <div style={{ position: 'relative' }}>
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Buscar productos..."
                                style={{
                                    width: '100%',
                                    padding: '12px 48px 12px 20px',
                                    borderRadius: 50,
                                    border: '2px solid var(--ec-border)',
                                    fontSize: '0.9rem'
                                }}
                                autoFocus
                            />
                            <Button
                                icon="pi pi-search"
                                className="ec-search-btn"
                                onClick={handleSearch}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Categories Bar - Desktop */}
            <div className="ec-navbar-categories">
                <div className="ec-container ec-navbar-categories-content">
                    <CategoryMegaMenu />
                </div>
            </div>

            {/* ====== CATEGORY DRAWER (estilo Coolbox) ====== */}
            <Sidebar
                visible={categoryDrawerVisible}
                onHide={() => setCategoryDrawerVisible(false)}
                position="left"
                className="ec-cat-drawer"
                showCloseIcon={false}
                style={{ width: '820px', padding: 0 }}
            >
                <div className="ec-cat-drawer-inner">
                    {/* Columna izquierda: lista de categorías */}
                    <div className="ec-cat-drawer-left">
                        <div className="ec-cat-drawer-header">
                            <span>Menú</span>
                            <button
                                className="ec-cat-drawer-close"
                                onClick={() => setCategoryDrawerVisible(false)}
                            >
                                <i className="pi pi-times" />
                            </button>
                        </div>
                        <div className="ec-cat-drawer-list">
                            {!loading && categories && categories.length > 0 ? (
                                categories.map(cat => (
                                    <div
                                        key={cat.id}
                                        className={`ec-cat-drawer-item ${hoveredCategory?.id === cat.id ? 'hovered' : ''}`}
                                        onMouseEnter={() => setHoveredCategory(cat)}
                                        onClick={() => handleCategoryClick(cat.id)}
                                    >
                                        <i className={cat.icono || cat.icon || 'pi pi-th-large'} style={{ color: cat.color }} />
                                        <span>{cat.nombre || cat.name || cat.descripcion}</span>
                                        <i className="pi pi-chevron-right ec-cat-drawer-arrow" />
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#999', padding: '12px' }}>
                                    {loading ? 'Cargando...' : 'Sin categorías'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Columna derecha: subcategorías + marcas */}
                    <div className="ec-cat-drawer-right">
                        {hoveredCategory && (
                            <>
                                <div className="ec-cat-drawer-right-header">
                                    <span
                                        className="ec-cat-drawer-right-title"
                                        onClick={() => handleCategoryClick(hoveredCategory.id)}
                                    >
                                        {hoveredCategory.descripcion || hoveredCategory.name || hoveredCategory.nombre}
                                    </span>
                                    <span
                                        className="ec-cat-drawer-right-seeall"
                                        onClick={() => handleCategoryClick(hoveredCategory.id)}
                                    >
                                        Ver todo
                                    </span>
                                </div>

                                <div className="ec-cat-drawer-right-body">
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        <p>Selecciona una categoría para ver sus productos</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Sidebar>

            {/* Mobile Sidebar Menu */}
            <Sidebar
                visible={mobileMenuVisible}
                onHide={() => setMobileMenuVisible(false)}
                position="left"
            >
                <div style={{ padding: '8px 0' }}>
                    <div className="ec-logo" style={{ marginBottom: 24 }}>
                        <div className="ec-logo-icon">
                            <i className="pi pi-bolt"></i>
                        </div>
                        <span className="ec-logo-text">{empresa?.nombre?.split(' ')[0]}<span>{empresa?.nombre?.split(' ')[1] || 'Store'}</span></span>
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <InputText
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch(e);
                                    setMobileMenuVisible(false);
                                }
                            }}
                            placeholder="Buscar..."
                            style={{ width: '100%', borderRadius: 50, padding: '10px 16px' }}
                        />
                    </div>

                    <h4 style={{ color: 'var(--ec-text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: 12 }}>
                        Categorías
                    </h4>
                    {!loading && categories && categories.length > 0 ? (
                        categories.map(cat => (
                            <div
                                key={cat.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '12px 8px',
                                    cursor: 'pointer',
                                    borderRadius: 8,
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => handleCategoryClick(cat.id)}
                            >
                                <i className={cat.icono || cat.icon || 'pi pi-th-large'} style={{ color: cat.color, fontSize: '1.1rem' }}></i>
                                <span style={{ fontWeight: 500 }}>{cat.nombre || cat.name || cat.descripcion}</span>
                            </div>
                        ))
                    ) : (
                        <div style={{ color: '#999', padding: '12px 8px' }}>
                            {loading ? 'Cargando categorías...' : 'Sin categorías'}
                        </div>
                    )}
                </div>
            </Sidebar>

            {/* Auth Dialog para clientes */}
            <AuthDialog
                visible={authDialogVisible}
                onHide={() => setAuthDialogVisible(false)}
            />

            {/* Favorites Sidebar */}
            {(() => {
                const favoritesHeaderContent = (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <i className="pi pi-heart" style={{ fontSize: '1.2rem', color: '#FF1744' }}></i>
                        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            Mis Favoritos ({favorites.length} {favorites.length === 1 ? 'favorito' : 'favoritos'})
                        </span>
                    </div>
                );

                return (
                    <Sidebar
                        visible={favoritesVisible}
                        onHide={() => setFavoritesVisible(false)}
                        position="right"
                        className="ec-favorites-sidebar"
                        style={{ width: '420px' }}
                        header={favoritesHeaderContent}
                    >
                        {favorites.length === 0 ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '60px 20px',
                                textAlign: 'center',
                                color: '#999'
                            }}>
                                <i className="pi pi-heart" style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.3 }}></i>
                                <p style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: 8, margin: '0 0 8px 0' }}>
                                    No tienes favoritos aún
                                </p>
                                <p style={{ opacity: 0.7, marginBottom: 24, margin: '0 0 24px 0' }}>
                                    Agrega productos a tu lista de favoritos
                                </p>
                                <Button
                                    label="Explorar productos"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    style={{ 
                                        background: 'linear-gradient(135deg, #0077B6 0%, #00B4D8 100%)',
                                        border: 'none',
                                        padding: '10px 28px',
                                        fontSize: '0.95rem'
                                    }}
                                    onClick={() => {
                                        setFavoritesVisible(false);
                                        navigate('/tienda');
                                    }}
                                />
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '16px', padding: '16px 20px 20px 20px' }}>
                                {favorites.map((product, idx) => {
                                    const displayPrice = product.price || product.precioVenta || product.precio || 0;
                                    const displayBrand = product.brand || product.marca || 'Producto';
                                    const displayName = product.name || product.nombre || 'Sin nombre';
                                    const displayImage = getProductImageUrl(product);
                                    const productId = product.id || product.idProducto;
                                    
                                    const handleProductClick = () => {
                                        setFavoritesVisible(false);
                                        navigate(`/tienda/producto/${productId}`);
                                    };
                                    
                                    return (
                                        <div key={idx} style={{
                                            padding: '12px',
                                            background: '#f8f9fa',
                                            borderRadius: '8px',
                                            border: '1px solid var(--ec-border)',
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'flex-start',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            ':hover': { background: '#fff' }
                                        }}>
                                            {/* Imagen pequeña - clickeable */}
                                            <div onClick={handleProductClick} style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '6px',
                                                background: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                border: '1px solid var(--ec-border)',
                                                overflow: 'hidden'
                                            }}>
                                                <img 
                                                    src={displayImage}
                                                    alt={displayName}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                    loading="lazy"
                                                />
                                            </div>
                                            
                                            {/* Info del producto - clickeable */}
                                            <div onClick={handleProductClick} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                                                <p style={{ 
                                                    fontSize: '0.8rem', 
                                                    color: 'var(--ec-text-light)', 
                                                    margin: '0 0 3px 0',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {displayBrand}
                                                </p>
                                                <p style={{ 
                                                    fontWeight: '600', 
                                                    margin: '0 0 6px 0',
                                                    fontSize: '0.95rem',
                                                    color: 'var(--ec-text)',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {displayName}
                                                </p>
                                                <p style={{ 
                                                    fontSize: '1rem', 
                                                    color: 'var(--ec-primary)',
                                                    fontWeight: '700',
                                                    margin: 0
                                                }}>
                                                    ${displayPrice.toFixed(2)}
                                                </p>
                                            </div>
                                            
                                            {/* Botón de eliminar */}
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-rounded p-button-text p-button-danger"
                                                style={{ 
                                                    padding: 0,
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                                onClick={() => {
                                                    const productId = product.id || product.idProducto;
                                                    toggleFavorite({ ...product, id: productId });
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Sidebar>
                );
            })()}
        </nav>
    );
};

export default EcommerceNavbar;
